import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import api, { inr, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";
const BASE = 250;

export default function AdminPricing() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/pricing").then(({ data }) => setRules({ markup_type: "pct_fixed", volumetric_divisor: 5000, ...data })).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/pricing", {
        markup_type: rules.markup_type,
        pct_markup: Number(rules.pct_markup), fixed_markup: Number(rules.fixed_markup),
        min_margin: Number(rules.min_margin), gst_pct: Number(rules.gst_pct),
        volumetric_divisor: Number(rules.volumetric_divisor),
      });
      toast.success("Pricing engine updated — applies to new rate checks and bookings immediately");
    } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
  };

  if (!rules) return null;
  const mtype = rules.markup_type || "pct_fixed";
  const pctAmt = ["pct", "pct_fixed"].includes(mtype) ? BASE * (Number(rules.pct_markup) || 0) / 100 : 0;
  const fixedAmt = ["fixed", "pct_fixed"].includes(mtype) ? Number(rules.fixed_markup) || 0 : 0;
  let marked = BASE + pctAmt + fixedAmt;
  if (marked - BASE < (Number(rules.min_margin) || 0)) marked = BASE + (Number(rules.min_margin) || 0);
  const gst = marked * ((Number(rules.gst_pct) || 0) / 100);

  return (
    <div className="mx-auto max-w-3xl" data-testid="admin-pricing-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Pricing Engine</h1>
      <p className="text-sm text-slate-500">Live courier API base price → ShipAny markup → GST → final customer price. One central engine powers the calculator and booking.</p>

      <form onSubmit={save} className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold text-slate-500">Markup type</label>
            <select data-testid="pricing-markup-type" className={`${inputCls} mt-1`} value={mtype} onChange={(e) => setRules({ ...rules, markup_type: e.target.value })}>
              <option value="pct_fixed">Percentage + Fixed</option>
              <option value="pct">Percentage only</option>
              <option value="fixed">Fixed only</option>
            </select>
            <p className="mt-1 text-[11px] text-slate-400">How the markup combines on the base courier price</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Volumetric divisor</label>
            <input data-testid="pricing-volumetric-divisor" type="number" step="1" min="1" className={`${inputCls} mono mt-1`} value={rules.volumetric_divisor} onChange={(e) => setRules({ ...rules, volumetric_divisor: e.target.value })} />
            <p className="mt-1 text-[11px] text-slate-400">Volumetric weight = L×W×H ÷ divisor (courier standard: 5000)</p>
          </div>
          {[["pct_markup", "Percentage markup (%)", "Applied on live API base price"], ["fixed_markup", "Fixed markup (₹)", "Flat amount per shipment"], ["min_margin", "Minimum margin (₹)", "Floor on ShipAny earnings"], ["gst_pct", "GST (%)", "Applied on marked-up price"]].map(([k, l, hint]) => (
            <div key={k}>
              <label className="text-xs font-semibold text-slate-500">{l}</label>
              <input data-testid={`pricing-${k}`} type="number" step="0.1" className={`${inputCls} mono mt-1`} value={rules[k]} onChange={(e) => setRules({ ...rules, [k]: e.target.value })} />
              <p className="mt-1 text-[11px] text-slate-400">{hint}</p>
            </div>
          ))}
        </div>
        <button data-testid="pricing-save-btn" disabled={saving} className="mt-6 flex items-center gap-2 rounded-xl bg-shipany-blue px-6 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-60 glow-blue">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Rules"}
        </button>
      </form>

      <div className="mt-6 rounded-2xl bg-shipany-navy p-6 text-white" data-testid="pricing-preview">
        <h2 className="font-display text-sm font-bold text-shipany-cyan">Live preview — ₹{BASE} courier API base price</h2>
        <div className="mono mt-4 space-y-2 text-sm">
          <div className="flex justify-between text-slate-300"><span>Base courier price (from live API)</span><span data-testid="preview-base">{inr(BASE)}</span></div>
          {pctAmt > 0 && <div className="flex justify-between text-slate-300"><span>+ {rules.pct_markup}% percentage markup</span><span data-testid="preview-pct">{inr(pctAmt)}</span></div>}
          {fixedAmt > 0 && <div className="flex justify-between text-slate-300"><span>+ fixed markup</span><span data-testid="preview-fixed">{inr(fixedAmt)}</span></div>}
          <div className="flex justify-between text-slate-300"><span>Subtotal (min margin {inr(Number(rules.min_margin) || 0)})</span><span>{inr(marked)}</span></div>
          <div className="flex justify-between text-slate-300"><span>+ GST {rules.gst_pct}%</span><span>{inr(gst)}</span></div>
          <div className="flex justify-between border-t border-white/10 pt-2 text-base font-bold text-shipany-cyan"><span>Customer pays</span><span data-testid="preview-final">{inr(marked + gst)}</span></div>
        </div>
        <p className="mt-4 text-[11px] text-slate-400">The original API base price is always preserved in the shipment pricing breakdown (admin/debug view).</p>
      </div>
    </div>
  );
}
